import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import HoursDialog from './HoursDialog';
import { getOpeningStatus, type Hours } from '../../utils/hours-helper';

interface OpeningStatusBadgeProps {
  hours?: Hours | null;
  locationName: string;
}

/**
 * Compact, dark pill showing the gym's current opening status (e.g. "Opens at 9:00").
 * Designed to sit as an overlay on top of the cover image. Opens the full hours
 * dialog when clicked. Renders nothing when no opening status is available.
 */
export default function OpeningStatusBadge({ hours, locationName }: OpeningStatusBadgeProps) {
  const { t } = useTranslation();
  const [showHoursDialog, setShowHoursDialog] = useState(false);
  const openingStatus = getOpeningStatus(hours);

  if (!openingStatus) return null;

  const openingStatusMessage = (() => {
    if (openingStatus.status === 'open') {
      return t('locationInfo.openUntil', { time: openingStatus.closeTime });
    }
    if (openingStatus.openDay === 'today') {
      return t('locationInfo.opensToday', { time: openingStatus.openTime });
    }
    if (openingStatus.openDay === 'tomorrow') {
      return t('locationInfo.opensTomorrow', { time: openingStatus.openTime });
    }
    return t('locationInfo.opensOn', {
      day: t(`days.${openingStatus.openDay}`),
      time: openingStatus.openTime,
    });
  })();

  return (
    <>
      <button
        type="button"
        onClick={() => setShowHoursDialog(true)}
        className="flex items-center gap-1 rounded-md bg-black/40 px-2 py-1 text-[11px] font-medium text-white transition-colors hover:bg-black/60"
      >
        <Clock className="h-3 w-3 shrink-0" />
        <span>{openingStatusMessage}</span>
      </button>

      <HoursDialog
        open={showHoursDialog}
        onOpenChange={setShowHoursDialog}
        hours={hours}
        title={`${t('locationInfo.openingHours')} - ${locationName}`}
      />
    </>
  );
}
